'use client';

import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { FilterSettingsMobileFilterDialogButtonV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsMobileFilterDialogButtonV2FragmentFragmentDoc';
import type { SelectedFilterValue } from '../../types';
import { FilterSettingsMobileFilterDialogButtonV2 } from '../FilterSettingsMobileFilterDialogButtonV2/FilterSettingsMobileFilterDialogButtonV2.client';
import { PRODUCT_LIST_ID } from '../../ids';
import { filterMessages } from '../FilterSettingsDesktop/FilterSettingsDesktop';

export type FilterSettingsMobileV2Props = {
  maskedData: FragmentType<typeof FilterSettingsMobileFilterDialogButtonV2FragmentFragmentDoc>;
  /** Callback fired when a user submits the selection of one or more filter values */
  onSubmit?: (filterValues: SelectedFilterValue[]) => void;
  /** If provided, renders the content of the categories dialog, don't forget to invoke the closeDialog callback on selection */
  renderCategoryDialogContent?: (closeDialog: () => void) => ReactNode;
  /** The list of currently selected filter values */
  selectedFilterValues: SelectedFilterValue[];
};

/**
 * A button that allows a user to select filter values on mobile using nested dialogs.
 *
 * @remarks
 * There can be multiple dialogs open at the same time
 * */

export const FilterSettingsMobileV2 = ({
  maskedData,
  selectedFilterValues: initiallySelectedFilterValues,
  onSubmit,
  renderCategoryDialogContent,
}: FilterSettingsMobileV2Props) => {
  const { formatMessage } = useIntl();

  return (
    // ATTENTION: multiple dialogs can be open at the same time! Filter selection works like a 2-step wizard
    <nav
      aria-label={formatMessage(filterMessages.filtersProductList)}
      aria-controls={PRODUCT_LIST_ID}
      className="flex flex-col gap-1 sm:flex-row"
    >
      <FilterSettingsMobileFilterDialogButtonV2
        maskedData={maskedData}
        selectedFilterValues={initiallySelectedFilterValues}
        onSubmit={onSubmit}
        renderCategoryDialogContent={renderCategoryDialogContent}
      />
    </nav>
  );
};
